import React from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Building2, User, Settings, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { OnboardingState } from '@/hooks/useOnboarding';
import { motion } from 'framer-motion';

interface ConfirmProps {
  state: OnboardingState;
  onConfirm: () => Promise<{ success: boolean; cluster_id?: string }>;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}

const item = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0 },
};

export const OnboardingConfirm: React.FC<ConfirmProps> = ({
  state, onConfirm, onBack, loading, error,
}) => {
  const isProvisioning = state.status === 'provisioning';

  const sections = [
    {
      icon: Building2,
      label: 'Empresa',
      items: [
        { k: 'Nome', v: state.empresa_nome },
        state.cnpj ? { k: 'CNPJ', v: state.cnpj } : null,
        state.segmento ? { k: 'Segmento', v: state.segmento } : null,
      ].filter(Boolean) as { k: string; v: string }[],
    },
    {
      icon: User,
      label: 'Administrador',
      items: [
        { k: 'Nome', v: state.user_name || '—' },
        { k: 'E-mail', v: state.user_email || '—' },
      ],
    },
    {
      icon: Settings,
      label: 'Configurações',
      items: [
        { k: 'Fuso horário', v: state.fuso_horario.replace('America/', '') },
        { k: 'Alerta QG', v: `${state.qg_alert_days} dia(s)` },
        { k: 'Alerta medição', v: `${state.medicao_alert_days} dia(s)` },
      ],
    },
  ];

  return (
    <motion.div
      className="space-y-5"
      initial="hidden"
      animate="show"
      variants={{ show: { transition: { staggerChildren: 0.06 } } }}
    >
      <motion.div variants={item}>
        <h2 className="text-xl font-semibold">Confirmar Configuração</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Revise os dados antes de prosseguir.
        </p>
      </motion.div>

      {error && (
        <motion.div variants={item}>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </motion.div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {sections.map(({ icon: Icon, label, items }) => (
          <motion.div
            key={label}
            variants={item}
            className="rounded-xl border bg-muted/30 p-4 space-y-2.5"
          >
            <div className="flex items-center gap-2 text-sm font-medium">
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon className="h-3.5 w-3.5 text-primary" />
              </div>
              {label}
            </div>
            <Separator />
            {items.map(({ k, v }) => (
              <div key={k} className="flex justify-between text-xs gap-2">
                <span className="text-muted-foreground whitespace-nowrap">{k}</span>
                <span className="font-medium truncate text-right">{v}</span>
              </div>
            ))}
          </motion.div>
        ))}
      </div>

      <motion.div variants={item} className="flex gap-3">
        <Button variant="outline" onClick={onBack} disabled={loading || isProvisioning} className="flex-1 group">
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Voltar
        </Button>
        <Button onClick={onConfirm} disabled={loading || isProvisioning} className="flex-1">
          {loading || isProvisioning ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Criando ambiente…</>
          ) : (
            <><CheckCircle2 className="h-4 w-4 mr-2" />Finalizar</>
          )}
        </Button>
      </motion.div>

      <motion.p variants={item} className="text-xs text-muted-foreground text-center">
        Ao finalizar, você concorda com os Termos de Uso e Política de Privacidade do NetUno.
      </motion.p>
    </motion.div>
  );
};
